import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "antd";
import { sendCustomerOtp, verifyCustomerOtp, resetOtpState } from "../../../redux/customer/customerSlice";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

// Reusable read-only/editable field
const Field = ({ label, value, name, editing, onChange }) => (
  <div>
    <label className="block font-semibold text-gray-500 mb-1">{label}</label>
    {editing ? (
      <input
        type="text"
        name={name}
        value={value || ""}
        onChange={onChange}
        className="w-full px-2 py-2 border-b border-[#06513D] focus:outline-none bg-transparent transition-colors"
      />
    ) : (
      <div className="w-full px-2 py-2 border-b border-gray-300 min-h-[40px] flex items-center text-gray-700">
        {value || "-"}
      </div>
    )}
  </div>
);

const ContactDetails = ({
  isEditing,
  contactDetails,
  handleBillingPassportFileSelect,
  handleBillingChange,
  onIdentityFileSelect,
  onBillingEmailVerified,
}) => {
  const dispatch = useDispatch();
  const { currentCustomer, loading, otpSent, otpVerified } = useSelector((state) => state.customer);
  const billing = currentCustomer?.billing || {};
  const customerId = currentCustomer?._id;
  const passportOrGovtId = billing.billingContact?.passportOrGovtId;

  // Local state for OTP input only
  const [otp, setOtp] = useState("");
  const [popup, setPopup] = useState({ visible: false, type: "success", title: "", message: "" });

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");

  const form = contactDetails || {};
  const handleBillingFieldChange = (e) => {
    if (e.target.name === "billingContact.email") {
      dispatch(resetOtpState());
      setOtp("");
    }
    handleBillingChange(e);
  };

  const billingContactEmail = form.billingContact?.email || "";
  const savedBillingEmail = billing.billingContact?.email || "";
  const pendingBillingEmail =
    billing.billingContact?.emailVerification?.pendingEmail || "";
  const emailChanged =
    billingContactEmail.trim().toLowerCase() !==
    savedBillingEmail.trim().toLowerCase();
  const hasPendingVerification =
    Boolean(pendingBillingEmail) &&
    pendingBillingEmail.trim().toLowerCase() ===
      billingContactEmail.trim().toLowerCase() &&
    !billing.billingContact?.emailVerification?.isVerified;
  const emailVerifiedFromApi =
    billing.billingContact?.emailVerification?.isVerified === true &&
    !emailChanged &&
    !hasPendingVerification;
  const showOtpInput = otpSent && !otpVerified;
  const showEmailVerified =
    (otpVerified || emailVerifiedFromApi) && !showOtpInput;

  const showPopup = (type, title, message) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => setPopup((prev) => ({ ...prev, visible: false })), 3000);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFileName(file.name);
    setUploadMsg("File selected. Click Save All.");

    if (handleBillingPassportFileSelect) {
      handleBillingPassportFileSelect(file);
    }

    if (e.target) e.target.value = "";
  };

  const handleSendCode = async () => {
    const otpEmail = billingContactEmail.trim();
    if (!otpEmail) {
      showPopup("error", "Error", "Please enter an email first.");
      return;
    }
    if (!customerId) {
      showPopup("error", "Error", "Customer ID is missing.");
      return;
    }

    const result = await dispatch(sendCustomerOtp({ id: customerId, email: otpEmail }));
    if (sendCustomerOtp.fulfilled.match(result)) {
      showPopup("success", "Success", "OTP sent successfully to " + otpEmail);
    } else {
      showPopup("error", "Error", result.payload || "Failed to send OTP.");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      showPopup("error", "Error", "Please enter the OTP.");
      return;
    }
    if (!customerId) {
      showPopup("error", "Error", "Customer ID is missing.");
      return;
    }

    const otpEmail = billingContactEmail.trim();
    if (!otpEmail) {
      showPopup("error", "Error", "Please enter an email first.");
      return;
    }

    const result = await dispatch(
      verifyCustomerOtp({ id: customerId, email: otpEmail, otp }),
    );
    if (verifyCustomerOtp.fulfilled.match(result)) {
      showPopup("success", "Success", "OTP Verified Successfully!");
      onBillingEmailVerified?.(otpEmail);
    } else {
      showPopup("error", "Error", result.payload || "Verification failed.");
    }
  };

  return (
    <div className="bg-white py-4 w-full">
      <h4 className="text-xl font-semibold text-[#06513D] mb-6">Contact Detail</h4>

      <div className="grid grid-cols-2 gap-8 mb-8">
        {/* Contact Person Name (Read-only view) */}
        <Field
          label="Contact Person Name"
          name="contactPersonName"
          value={form.contactPersonName}
          editing={isEditing}
          onChange={handleBillingFieldChange}
        />

        {/* Identity Proof Section */}
        <div>
          <label className="block font-semibold text-gray-500 mb-1">
            Passport / Govt Issued ID (Identity Proof)
          </label>
          <div className="flex gap-4 items-center pt-2">
            <div className="flex-1 px-2 py-2 border-b border-gray-300 text-gray-600 truncate">
              {selectedFileName || passportOrGovtId?.filePath?.split("/").pop() || "No file uploaded"}
            </div>

            <div className="flex gap-2 shrink-0">
              {passportOrGovtId?.filePath && (
                <Button
                  type="primary"
                  className="bg-[#06513D] text-white text-xs h-8 px-6 rounded-md hover:bg-[#054031]"
                  onClick={() => {
                    const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:8081").replace(/\/$/, "");
                    const fullUrl = passportOrGovtId.filePath.startsWith("http")
                      ? passportOrGovtId.filePath
                      : `${baseUrl}/${passportOrGovtId.filePath.replace(/^\//, "")}`;
                    window.open(fullUrl, "_blank");
                  }}
                >
                  VIEW
                </Button>
              )}


              {isEditing && (
                <>
                  <input
                    type="file"
                    ref={fileRef}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                  />
                  <Button
                    type="default"
                    loading={uploading}
                    className="text-[#06513D] border-[#06513D] text-xs h-8 px-6 rounded-md"
                    onClick={() => fileRef.current?.click()}
                  >
                    {uploading ? "..." : "UPLOAD"}
                  </Button>
                </>
              )}
            </div>
          </div>
          {uploadMsg && (
            <p className={`text-[10px] mt-1 ${uploadMsg.includes("Success") ? "text-green-600" : "text-red-500"}`}>
              {uploadMsg}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8 mb-8">
        {/* Contact Number (Read-only view) */}
        <div>
          <label className="block font-semibold text-gray-500 mb-1">Contact Number</label>
          <div className="flex gap-2">
            <div className="w-20">
              <Field
                label="Code"
                name="countryCode"
                value={form.countryCode}
                editing={isEditing}
                onChange={handleBillingFieldChange}
              />
            </div>
            <div className="flex-1">
              <Field
                label="Number"
                name="contactNumber"
                value={form.contactNumber}
                editing={isEditing}
                onChange={handleBillingFieldChange}
              />
            </div>
          </div>
        </div>

        {/* Email Field - Editable with OTP verification when isEditing is true */}
        {isEditing ? (
          <div>
            <label className="block font-semibold text-gray-500 mb-1">Email ID</label>
            <div className="flex gap-2 mb-4">
              <input
                type="email"
                name="billingContact.email"
                placeholder="Enter Email Address"
                value={form.billingContact?.email || ""}
                onChange={handleBillingFieldChange}
                className="flex-1 px-2 py-2 border-b border-[#06513D] focus:outline-none bg-transparent"
              />
              <button
                onClick={!loading ? handleSendCode : undefined}
                className={`h-[40px] px-4 rounded-md border flex items-center justify-center transition-colors text-xs font-semibold ${otpSent
                  ? "bg-[#06513D] text-white border-[#06513D]"
                  : "text-[#06513D] border-[#06513D] hover:bg-[#06513D] hover:text-white"
                  } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {loading && !otpSent ? "Sending..." : otpSent ? "Resend Code" : "Send Code"}
              </button>
            </div>

            {/* OTP Input Row */}
            {showOtpInput && (
              <div className="flex gap-2 animate-fade-in">
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-24 px-2 py-2 border-b border-[#06513D] focus:outline-none bg-transparent"
                />
                <button
                  onClick={!loading ? handleVerifyOtp : undefined}
                  className={`h-[40px] px-4 rounded-md border flex items-center justify-center transition-colors text-xs font-semibold ${otpVerified
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-[#06513D] text-white border-[#06513D] hover:bg-[#054031]"
                    } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {loading ? "Verifying..." : "Verify"}
                </button>
              </div>
            )}

            {emailChanged && !showEmailVerified && (
              <p className="text-amber-600 text-xs mt-2">
                Verify this email before saving. Unverified changes will not be saved.
              </p>
            )}

            {showEmailVerified && (
              <div className="flex items-center gap-2 text-green-600 mt-2 text-sm font-medium">
                <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">✓</div>
                Email Verified Successfully
              </div>
            )}
          </div>
        ) : (
          <Field
            label="Email ID"
            value={billing.billingContact?.email}
            editing={false}
          />
        )}
      </div>

      {/* Popup Notification */}
      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
            onClose={() => setPopup((prev) => ({ ...prev, visible: false }))}
          />
        </div>
      )}
    </div>

  );
};

export default ContactDetails;











































